import { useEffect } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $getSelection,
  $isRangeSelection,
  $getRoot,
  SELECTION_CHANGE_COMMAND,
  COMMAND_PRIORITY_LOW,
  $isTextNode,
} from 'lexical';
import { $isHeadingNode } from '@lexical/rich-text';
import { mergeRegister } from '@lexical/utils';

const EmptyBlockPlaceholderPlugin = () => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    const rootEl = editor.getRootElement();

    const clearPlaceholders = () => {
      const scope = rootEl || document;
      const all = scope.querySelectorAll('[data-lexical-placeholder]');
      all.forEach((el) => el.removeAttribute('data-lexical-placeholder'));
    };

    const applyForState = (editorState) => {
      editorState.read(() => {
        clearPlaceholders();

        const selection = $getSelection();
        if (!$isRangeSelection(selection)) return;

        // Find the nearest element node that corresponds to a DOM element (paragraph, list item, heading, etc.)
        let node = selection.anchor.getNode();
        let element = $isTextNode(node) ? node.getParent() : node;
        while (element && !editor.getElementByKey(element.getKey())) {
          element = element.getParent();
        }
        if (!element) return;

        const dom = editor.getElementByKey(element.getKey());
        if (!dom) return;

        const isEmpty = element.getTextContent().trim() === '';
        if (isEmpty) {
          const root = $getRoot();
          const parent = element.getParent();

          // Only show placeholders for direct children of root
          // (not for paragraphs inside callout, toggle, column, etc.)
          const isTopLevel = parent === root;

          if ($isHeadingNode(element) && isTopLevel) {
            const tag = element.getTag();
            const level = tag === 'h1' ? '1' : tag === 'h2' ? '2' : '3';
            dom.setAttribute('data-lexical-placeholder', `Heading ${level}`);
          } else if (isTopLevel) {
            const isOnlyBlock = root.getChildrenSize() === 1 && root.getFirstChild() === element;
            const placeholderText = isOnlyBlock
              ? "Press '/' for commands, or start typing..."
              : "Type '/' for commands";
            dom.setAttribute('data-lexical-placeholder', placeholderText);
          }
          // Nested blocks (inside callout, toggle, column, etc.) get no placeholder
        }
      });
    };

    const unregister = mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        applyForState(editorState);
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          applyForState(editor.getEditorState());
          return false;
        },
        COMMAND_PRIORITY_LOW
      )
    );

    // Initial paint
    applyForState(editor.getEditorState());

    return unregister;
  }, [editor]);

  return null;
};

export default EmptyBlockPlaceholderPlugin;

